import { buildIntegrationPrompt } from '@admin-platform/shared/integration-content';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { endpointsForPage } from '../api-endpoints';
import { AnchorHeading } from '../components/AnchorHeading';
import { Figure, Flow } from '../components/Diagram';
import { OnThisPage } from '../components/OnThisPage';
import { useDocsPage } from '../hooks/useDocsPage';

const NODE_SIGN = `const crypto = require('crypto');

function signRequest(body, signingSecret) {
  return crypto
    .createHmac('sha256', signingSecret)
    .update(body) // raw bytes — do not re-serialize
    .digest('hex');
}`;

const NODE_FULL = `const crypto = require('crypto');
const https  = require('https');

const API_KEY     = 'rvly_sk_live_<your-token>';
const SIGN_SECRET = '<your-signing-secret>';
const HOST        = 'api.revvly.ai';

const payload = JSON.stringify({
  events: [
    {
      eventId:        '01HYZ7K2N8V3WXPQ4JCBM5FGTE', // ULID recommended
      subscriptionId: '663b1f2e4a3c2d1e0f8b7a9c',
      variableId:     '663b1f2e4a3c2d1e0f8b7b01',
      type:           'USAGE_REPORTED',
      quantity:       1,
      reportedAt:     new Date().toISOString(),
    },
  ],
});

const sig = crypto
  .createHmac('sha256', SIGN_SECRET)
  .update(payload)
  .digest('hex');

const req = https.request({
  host:   HOST,
  path:   '/api/v1/events',
  method: 'POST',
  headers: {
    'Content-Type':       'application/json',
    'Content-Length':     Buffer.byteLength(payload),
    'Authorization':      \`Bearer \${API_KEY}\`,
    'X-Signature-SHA256': \`sha256=\${sig}\`,
  },
}, (res) => {
  let data = '';
  res.on('data', (chunk) => { data += chunk; });
  res.on('end', () => console.log(JSON.parse(data)));
});

req.write(payload);
req.end();`;

const PYTHON_FULL = `import hashlib, hmac, json, urllib.request

API_KEY     = 'rvly_sk_live_<your-token>'
SIGN_SECRET = '<your-signing-secret>'
URL         = 'https://api.revvly.ai/api/v1/events'

payload = json.dumps({
    'events': [
        {
            'eventId':        '01HYZ7K2N8V3WXPQ4JCBM5FGTE',
            'subscriptionId': '663b1f2e4a3c2d1e0f8b7a9c',
            'variableId':     '663b1f2e4a3c2d1e0f8b7b01',
            'type':           'USAGE_REPORTED',
            'quantity':       1,
            'reportedAt':     '2026-05-03T12:00:00Z',
        }
    ]
}, separators=(',', ':')).encode()

sig = hmac.new(SIGN_SECRET.encode(), payload, hashlib.sha256).hexdigest()

req = urllib.request.Request(URL, data=payload, headers={
    'Content-Type':       'application/json',
    'Authorization':      f'Bearer {API_KEY}',
    'X-Signature-SHA256': f'sha256={sig}',
})
with urllib.request.urlopen(req) as res:
    print(json.load(res))`;

const CURL_FULL = `# Store these in environment variables — never commit secrets.
export REVVLY_TOKEN="rvly_sk_live_<your-token>"
export REVVLY_SECRET="<your-signing-secret>"

BODY='{"events":[{"eventId":"01HYZ7K2N8V3WXPQ4JCBM5FGTE","subscriptionId":"663b1f2e4a3c2d1e0f8b7a9c","variableId":"663b1f2e4a3c2d1e0f8b7b01","type":"USAGE_REPORTED","quantity":1,"reportedAt":"2026-05-03T12:00:00Z"}]}'

SIG=$(echo -n "$BODY" | openssl dgst -sha256 -hmac "$REVVLY_SECRET" | awk '{print $2}')

curl -s -X POST https://api.revvly.ai/api/v1/events \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer $REVVLY_TOKEN" \\
  -H "X-Signature-SHA256: sha256=$SIG" \\
  -d "$BODY"`;

const RESPONSE_EXAMPLE = `HTTP/1.1 202 Accepted

{
  "results": [
    {
      "eventId": "01HYZ7K2N8V3WXPQ4JCBM5FGTE",
      "status": "PROCESSED",
      "currentCount": 42
    },
    {
      "eventId": "01HYZ7K2N8V3WXPQ4JCBM5FGTF",
      "status": "DUPLICATE"
    },
    {
      "eventId": "01HYZ7K2N8V3WXPQ4JCBM5FGTG",
      "status": "REJECTED",
      "rejectionReason": "reportedAt is more than 7 days in the past"
    }
  ]
}`;

// Shared with the dashboard's copy-prompt button so an operator and an agent
// read the same contract. Edit the guide in libs/shared/integration-content.
const AGENT_PROMPT = buildIntegrationPrompt('eventIngestion');

type Lang = 'node' | 'python' | 'curl';

export function EventIngestion() {
  const [lang, setLang] = useState<Lang>('node');
  useDocsPage('Event Ingestion API — Revvly Docs');

  const codeByLang: Record<Lang, string> = {
    node: NODE_FULL,
    python: PYTHON_FULL,
    curl: CURL_FULL,
  };

  return (
    <div className="tn-page">
      <nav className="dp-crumbs" aria-label="Breadcrumb">
        <Link to="/docs/api">API</Link>
        <span aria-hidden="true">›</span>
        <span className="dp-crumb-current">Event Ingestion</span>
      </nav>
      <header className="dp-header">
        <span className="rv-eyebrow-mini">API Reference</span>
        <h1>
          Report usage <em>events</em> to Revvly.
        </h1>
        <p className="dp-oneliner">
          Stream metered usage &mdash; API calls, token consumption, seat changes,
          commissions &mdash; directly from your application. Events are validated,
          deduplicated, and applied to your subscriptions in real time.
        </p>
      </header>

      <OnThisPage endpoints={endpointsForPage('/docs/api/events')} />

      {/* ── Authentication ─────────────────────────────────────── */}
      <section className="dp-section">
        <h2>Authentication</h2>
        <p className="dp-section-sub">
          Every request requires two credentials issued together when you create or
          rotate an API key. Both are shown once and never again &mdash; capture them
          before closing the dialog.
        </p>

        <div className="dp-fields-table api-field-table-mb-sm">
          <div className="dp-field-row">
            <div className="dp-field-k">Bearer token</div>
            <div className="dp-field-v">
              Sent in the <span className="api-inline">Authorization</span> header.
              Format: <span className="api-inline">rvly_sk_live_&lt;22 chars&gt;</span>.
              Identifies your tenant and verifies the caller.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Signing secret</div>
            <div className="dp-field-v">
              Used to compute the <span className="api-inline">X-Signature-SHA256</span>{' '}
              HMAC over the raw request body. Prevents event injection even if the
              Bearer token is compromised.
            </div>
          </div>
        </div>

        <div className="api-note">
          <span className="api-note-icon">i</span>
          <span>
            Issue API keys from <strong>Settings &rarr; API Keys</strong> in the Revvly
            dashboard. When you scope a key to specific subscriptions and variables,
            events outside that scope are rejected with status{' '}
            <span className="api-inline">REJECTED</span>.
          </span>
        </div>

        <span className="api-code-label">Request headers</span>
        <pre className="api-code">{`Authorization:      Bearer rvly_sk_live_<your-token>
Content-Type:       application/json
X-Signature-SHA256: sha256=<hmac-hex>`}</pre>
      </section>

      <section className="dp-section">
        <h2>Give this prompt to an integration agent</h2>
        <p className="dp-plain">
          Copy this into an agent that is building a metering or commission-event integration.
        </p>
        <pre className="api-code">{AGENT_PROMPT}</pre>
      </section>

      {/* ── Endpoint ───────────────────────────────────────────── */}
      <section className="dp-section">
        <AnchorHeading id="post-events">Endpoint</AnchorHeading>
        <div className="api-endpoint-bar">
          <span className="api-method api-method-post">POST</span>
          <span className="api-url">https://api.revvly.ai/api/v1/events</span>
          <span className="api-returns">202 Accepted</span>
        </div>
        <p className="dp-section-sub">
          Submit a batch of up to 1&thinsp;000 events per request. Each event is
          processed independently &mdash; one rejected event does not block the others.
          The response always returns one result object per submitted event.
        </p>
      </section>

      {/* ── Request body ───────────────────────────────────────── */}
      <section className="dp-section">
        <h2>Request body</h2>
        <span className="api-code-label">Shape</span>
        <pre className="api-code">{`{
  "events": [Event, ...]   // 1 – 1 000 items
}`}</pre>

        <h3 className="api-sub-heading">Event fields</h3>

        <div className="dp-fields-table api-field-table-mb">
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">eventId</span>
              <span className="api-badge api-badge-req">required</span>
            </div>
            <div className="dp-field-v">
              Client-generated unique identifier. Revvly uses this for deduplication &mdash; a
              second event with the same <span className="api-inline">eventId</span> returns{' '}
              <span className="api-inline">DUPLICATE</span> without re-applying the delta.
              ULIDs are recommended for sortability.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">subscriptionId</span>
              <span className="api-badge api-badge-cond">one of</span>
            </div>
            <div className="dp-field-v">
              The internal Revvly subscription id this usage belongs to. Supply{' '}
              <strong>either</strong> this <strong>or</strong>{' '}
              <span className="api-inline">externalSubscriptionId</span> &mdash; exactly one
              of the pair. Find it in the dashboard, via the GraphQL{' '}
              <span className="api-inline">subscriptions</span> query, or via{' '}
              <span className="api-inline">GET https://api.revvly.ai/api/v1/subscriptions</span>.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">externalSubscriptionId</span>
              <span className="api-badge api-badge-cond">one of</span>
            </div>
            <div className="dp-field-v">
              Your own reference for the subscription &mdash; the{' '}
              <span className="api-inline">externalId</span> you set when the subscription
              was created (unique per tenant). Use this <strong>instead of</strong>{' '}
              <span className="api-inline">subscriptionId</span> so you never have to store a
              Revvly id. A value that doesn&rsquo;t resolve produces a per-event{' '}
              <span className="api-inline">REJECTED</span> result.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">variableId</span>
              <span className="api-badge api-badge-cond">one of</span>
            </div>
            <div className="dp-field-v">
              The internal id of the meter (seat, call, token, funnel) to report against. Supply{' '}
              <strong>either</strong> this <strong>or</strong>{' '}
              <span className="api-inline">variableSlug</span> &mdash; exactly one of the pair.
              The meter must be attached to the subscription through a Variable Component
              &mdash; either inherited from the pinned module/bundle or attached directly.
              (Field name kept as <span className="api-inline">variableId</span> for backward
              compatibility with the pre-rename API.)
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">variableSlug</span>
              <span className="api-badge api-badge-cond">one of</span>
            </div>
            <div className="dp-field-v">
              The tenant-unique slug of the meter (e.g.{' '}
              <span className="api-inline">api-calls</span>). Use this <strong>instead of</strong>{' '}
              <span className="api-inline">variableId</span>. Discover available slugs via{' '}
              <span className="api-inline">GET https://api.revvly.ai/api/v1/variables</span> (endpoint path unchanged
              by the rename). A slug that
              doesn&rsquo;t resolve produces a per-event{' '}
              <span className="api-inline">REJECTED</span> result.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">type</span>
              <span className="api-badge api-badge-cond">optional</span>
            </div>
            <div className="dp-field-v">
              Defaults to <span className="api-inline">USAGE_REPORTED</span> when omitted. Any
              other value is rejected per-event.
              <br />
              <span className="api-inline">USAGE_REPORTED</span> &mdash; add or remove usage
              (sign of <span className="api-inline">quantity</span> controls direction).
              <br />
              <span className="api-inline">USAGE_CORRECTED</span> &mdash; the same application
              path, tagged as a correction in the audit trail. Also needs{' '}
              <span className="api-inline">quantity</span>.
              <br />
              <span className="api-inline">COMMISSION_EVENT</span> &mdash; record a sale for
              revenue-share billing.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">reportedAt</span>
              <span className="api-badge api-badge-req">required</span>
            </div>
            <div className="dp-field-v">
              ISO 8601 timestamp for when the usage occurred. Must be within the last
              7 days and no more than 15 minutes in the future. Events older than 24 h
              are accepted but flagged as late arrivals.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">quantity</span>
              <span className="api-badge api-badge-cond">USAGE_REPORTED</span>
            </div>
            <div className="dp-field-v">
              Integer or decimal delta. Positive adds usage, negative removes it.
              Required when <span className="api-inline">type</span> is{' '}
              <span className="api-inline">USAGE_REPORTED</span>.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">saleAmount</span>
              <span className="api-badge api-badge-cond">COMMISSION_EVENT</span>
            </div>
            <div className="dp-field-v">
              Sale value in the subscription&rsquo;s billing currency. Negative for
              refunds. Required when <span className="api-inline">type</span> is{' '}
              <span className="api-inline">COMMISSION_EVENT</span>.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">transactionId</span>
              <span className="api-badge api-badge-cond">COMMISSION_EVENT</span>
            </div>
            <div className="dp-field-v">
              Your internal transaction reference. Required for{' '}
              <span className="api-inline">COMMISSION_EVENT</span>; used for reconciliation.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">context</span>
              <span className="api-badge api-badge-opt">optional</span>
            </div>
            <div className="dp-field-v">
              Arbitrary key/value metadata. Stored on the event record for debugging
              and dispute investigation. Not used in billing calculations.
            </div>
          </div>
        </div>

        <div className="api-note">
          <span className="api-note-icon">i</span>
          <span>
            <strong>Identify the target with exactly one of each pair.</strong> Each event
            must carry either <span className="api-inline">subscriptionId</span> or{' '}
            <span className="api-inline">externalSubscriptionId</span> (not both, not neither),
            and either <span className="api-inline">variableId</span> or{' '}
            <span className="api-inline">variableSlug</span>. The same one-of rule applies to
            the usage endpoints (<span className="api-inline">https://api.revvly.ai/api/v1/usage</span>,{' '}
            <span className="api-inline">https://api.revvly.ai/api/v1/usage/set</span>,{' '}
            <span className="api-inline">https://api.revvly.ai/api/v1/usage/commission</span>,{' '}
            <span className="api-inline">https://api.revvly.ai/api/v1/usage/batch</span>), where an unresolved reference
            returns a <span className="api-inline">404</span> /{' '}
            <span className="api-inline">400</span> instead.
          </span>
        </div>

        <h3 className="api-sub-heading">Where do these IDs come from?</h3>
        <p className="dp-section-sub">
          You don&rsquo;t have to store Revvly&rsquo;s internal Mongo ids at all. The
          recommended pattern is to set <span className="api-inline">externalId</span> on the
          subscription at creation &mdash; your own account or subscription id &mdash; and then
          always report usage with <span className="api-inline">externalSubscriptionId</span>.
          Discover a meter&rsquo;s <span className="api-inline">variableSlug</span> (or its
          internal <span className="api-inline">variableId</span>) once via{' '}
          <span className="api-inline">GET https://api.revvly.ai/api/v1/variables</span>. If you&rsquo;d rather sync
          and store Revvly ids, list subscriptions with{' '}
          <span className="api-inline">GET https://api.revvly.ai/api/v1/subscriptions</span>. Both the pattern and
          the discovery endpoints are covered in full on the{' '}
          <Link to="/docs/api/external-references">External references &amp; ID discovery</Link>{' '}
          page.
        </p>
      </section>

      {/* ── Signing the request ────────────────────────────────── */}
      <section className="dp-section">
        <h2>Signing the request</h2>
        <p className="dp-section-sub">
          Compute HMAC-SHA256 over the exact bytes you send as the request body,
          using the signing secret as the key. Pass the result as a hex string in
          the <span className="api-inline">X-Signature-SHA256</span> header.
        </p>

        <div className="api-note">
          <span className="api-note-icon">i</span>
          <span>
            Sign the <strong>raw body bytes</strong>, not a re-serialized copy.
            Any whitespace or key-order difference will invalidate the signature.
            Construct your JSON string once, sign it, then send that same string.
          </span>
        </div>

        <div className="api-code-wrap">
          <span className="api-code-label">HMAC signature &mdash; Node.js</span>
          <pre className="api-code">{NODE_SIGN}</pre>
        </div>

        <div className="dp-fields-table">
          <div className="dp-field-row">
            <div className="dp-field-k">Algorithm</div>
            <div className="dp-field-v">HMAC-SHA256</div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Key</div>
            <div className="dp-field-v">
              Your signing secret (plaintext, not base64-encoded)
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Data</div>
            <div className="dp-field-v">Raw request body bytes</div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Header format</div>
            <div className="dp-field-v">
              <span className="api-inline">X-Signature-SHA256: sha256=&lt;hex&gt;</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── Full examples ──────────────────────────────────────── */}
      <section className="dp-section">
        <h2>Full examples</h2>
        <p className="dp-section-sub">
          A complete request &mdash; authentication, signature, and a single{' '}
          <span className="api-inline">USAGE_REPORTED</span> event.
        </p>

        <div className="api-tabs">
          {(['node', 'python', 'curl'] as Lang[]).map((l) => (
            <button
              key={l}
              className={`api-tab ${lang === l ? 'api-tab-active' : ''}`}
              onClick={() => setLang(l)}
            >
              {l === 'node' ? 'Node.js' : l === 'python' ? 'Python' : 'cURL'}
            </button>
          ))}
        </div>
        <pre className="api-code api-code-tabbed">{codeByLang[lang]}</pre>
      </section>

      {/* ── Response ───────────────────────────────────────────── */}
      <section className="dp-section">
        <h2>Response</h2>
        <p className="dp-section-sub">
          Revvly returns one result object per submitted event, in submission order.
          A <span className="api-inline">202 Accepted</span> status is returned even
          when individual events are rejected &mdash; inspect each result&rsquo;s{' '}
          <span className="api-inline">status</span> field.
        </p>

        <div className="api-code-wrap">
          <pre className="api-code">{RESPONSE_EXAMPLE}</pre>
        </div>

        <Figure
          title="What happens to a batch"
          caption="Each event gets its own outcome, so one rejection never blocks the rest of the batch."
        >
          <Flow
            steps={[
              { title: 'Signed request', text: 'Bearer key plus HMAC of the raw body.' },
              { title: 'Batch accepted', text: 'Up to 1,000 events. More returns 400.' },
              {
                fork: [
                  { title: 'PROCESSED', text: 'Event applied to the subscription.', tone: 'accent' },
                  { title: 'DUPLICATE', text: 'Same eventId already received. No delta.', tone: 'muted' },
                  { title: 'REJECTED', text: 'Failed validation or scope. A reason is given.', tone: 'danger' },
                ],
              },
              { title: '202 Accepted', text: 'One result per event, in submission order.' },
            ]}
          />
        </Figure>

        <div className="api-status-row">
          <div className="api-status-item">
            <span className="api-status-pill api-status-pill-ok">PROCESSED</span>
            <span className="api-status-desc">
              Event applied successfully.{' '}
              <span className="api-inline">currentCount</span> reflects the
              subscription&rsquo;s usage total after this delta.
            </span>
          </div>
          <div className="api-status-item">
            <span className="api-status-pill api-status-pill-dup">DUPLICATE</span>
            <span className="api-status-desc">
              An event with this <span className="api-inline">eventId</span> was already
              received. The original result stands; no delta is re-applied.
            </span>
          </div>
          <div className="api-status-item">
            <span className="api-status-pill api-status-pill-err">REJECTED</span>
            <span className="api-status-desc">
              Event failed validation.{' '}
              <span className="api-inline">rejectionReason</span> explains why. Other
              events in the same batch are unaffected.
            </span>
          </div>
        </div>
      </section>

      {/* ── Validation rules ───────────────────────────────────── */}
      <section className="dp-section">
        <h2>Validation rules</h2>
        <div className="dp-fields-table">
          <div className="dp-field-row">
            <div className="dp-field-k">Batch size</div>
            <div className="dp-field-v">
              Maximum 1&thinsp;000 events per request. Exceeding this returns 400 Bad
              Request before any events are processed.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Clock skew</div>
            <div className="dp-field-v">
              <span className="api-inline">reportedAt</span> must not be more than
              15 minutes in the future. Events further ahead are{' '}
              <span className="api-inline">REJECTED</span>.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Stale events</div>
            <div className="dp-field-v">
              <span className="api-inline">reportedAt</span> must not be more than
              7 days in the past. Older events are{' '}
              <span className="api-inline">REJECTED</span>.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Late arrivals</div>
            <div className="dp-field-v">
              Events with <span className="api-inline">reportedAt</span> older than
              24 h are accepted and applied, but flagged internally as late arrivals
              for operator visibility.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Scope check</div>
            <div className="dp-field-v">
              If the API key is scoped to specific subscriptions or meters, events
              targeting anything outside that scope are{' '}
              <span className="api-inline">REJECTED</span>. Unscoped keys accept any
              subscription and meter belonging to your tenant.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Idempotency</div>
            <div className="dp-field-v">
              Safe to retry with the same <span className="api-inline">eventId</span>.
              Revvly deduplicates globally &mdash; re-sending an event returns{' '}
              <span className="api-inline">DUPLICATE</span> without double-counting.
            </div>
          </div>
        </div>
      </section>

      {/* ── Key rotation ───────────────────────────────────────── */}
      <section className="dp-section">
        <h2>Key rotation</h2>
        <p className="dp-section-sub">
          Rotate an API key from the dashboard or via the{' '}
          <span className="api-inline">rotateApiKey</span> GraphQL mutation. After
          rotation, both the old and new key remain valid for 30 days, giving you
          time to deploy the replacement signing secret without downtime.
        </p>
        <div className="api-note api-note-warn">
          <span className="api-note-icon">&#9888;</span>
          <span>
            The new plaintext token and signing secret are shown once at rotation.
            Store them in your secret manager before dismissing the dialog &mdash; they
            cannot be retrieved after the rotation completes.
          </span>
        </div>
      </section>
    </div>
  );
}
